import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { TrendingUp, BarChart3 } from 'lucide-react';
import { monthlyTrends, salesMetrics } from '../../data/mockData';

const TrendsChart = () => {
  const [chartType, setChartType] = useState('spend'); // 'spend' or 'growth'

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const maxSpend = Math.max(...monthlyTrends.map(m => Math.max(m.planned, m.actual)));
  const maxGrowth = Math.max(...monthlyTrends.map(m => m.salesGrowth));

  return (
    <Card className="shadow-lg border-l-4 border-l-emerald-500">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
            Monthly Performance Trends
          </CardTitle>
          <div className="flex space-x-2">
            <Button
              variant={chartType === 'spend' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('spend')}
              className={`text-xs ${chartType === 'spend' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              Spend
            </Button>
            <Button
              variant={chartType === 'growth' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('growth')}
              className={`text-xs ${chartType === 'growth' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            >
              <TrendingUp className="h-4 w-4 mr-1" />
              Growth
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          {chartType === 'spend' ? (
            <div className="h-full flex items-end justify-between gap-1">
              {monthlyTrends.map((month, index) => {
                const plannedHeight = (month.planned / maxSpend) * 280;
                const actualHeight = (month.actual / maxSpend) * 280;
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="flex items-end space-x-1 mb-2" style={{ height: '280px' }}>
                      <div className="relative group">
                        <div 
                          className="w-6 bg-gradient-to-t from-gray-400 to-gray-300 rounded-t-sm opacity-60 hover:opacity-80 transition-opacity duration-200"
                          style={{ height: `${plannedHeight}px` }}
                        >
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              Planned: {formatCurrency(month.planned)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="relative group">
                        <div 
                          className={`w-6 bg-gradient-to-t ${
                            month.actual > month.planned 
                              ? 'from-red-400 to-red-300' 
                              : 'from-emerald-400 to-emerald-300'
                          } rounded-t-sm hover:shadow-md transition-all duration-200 border border-white shadow-sm`}
                          style={{ height: `${actualHeight}px` }}
                        >
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              Actual: {formatCurrency(month.actual)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-700">{month.month}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-full relative">
              <svg className="w-full h-full">
                <defs>
                  <linearGradient id="growthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgb(34, 197, 94)" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="rgb(14, 165, 233)" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                
                {/* Grid lines */}
                {[0, 25, 50, 75, 100].map((percent) => (
                  <line
                    key={percent}
                    x1="0"
                    y1={`${percent}%`}
                    x2="100%"
                    y2={`${percent}%`}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                  />
                ))}
                
                {/* Area fill */}
                <path
                  d={`M 0,${280 - (monthlyTrends[0].salesGrowth / maxGrowth) * 280} ${monthlyTrends
                    .map((month, index) => {
                      const x = (index / (monthlyTrends.length - 1)) * 100;
                      const y = 280 - (month.salesGrowth / maxGrowth) * 280;
                      return `L ${x}%,${y}`;
                    })
                    .join(' ')} L 100%,280 L 0,280 Z`}
                  fill="url(#growthGradient)"
                />
                
                {/* Line */}
                <path
                  d={`M 0,${280 - (monthlyTrends[0].salesGrowth / maxGrowth) * 280} ${monthlyTrends
                    .map((month, index) => {
                      const x = (index / (monthlyTrends.length - 1)) * 100;
                      const y = 280 - (month.salesGrowth / maxGrowth) * 280;
                      return `L ${x}%,${y}`;
                    })
                    .join(' ')}`}
                  fill="none"
                  stroke="rgb(59, 130, 246)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {/* Data points */}
                {monthlyTrends.map((month, index) => {
                  const x = (index / (monthlyTrends.length - 1)) * 100;
                  const y = 280 - (month.salesGrowth / maxGrowth) * 280;
                  return (
                    <g key={index}>
                      <circle
                        cx={`${x}%`}
                        cy={y}
                        r="4"
                        fill="rgb(59, 130, 246)"
                        stroke="white"
                        strokeWidth="2"
                        className="hover:r-6 transition-all duration-200 cursor-pointer"
                      />
                      <text
                        x={`${x}%`}
                        y={y - 10}
                        textAnchor="middle"
                        className="text-xs fill-blue-600 opacity-0 hover:opacity-100 transition-opacity duration-200"
                      >
                        {month.salesGrowth}%
                      </text>
                    </g>
                  );
                })}
              </svg>
              
              {/* Month labels */}
              <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2">
                {monthlyTrends.map((month, index) => (
                  <div key={index} className="text-xs text-gray-600 font-medium">
                    {month.month}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4 flex justify-center space-x-6 text-sm">
          {chartType === 'spend' ? (
            <>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gradient-to-r from-gray-400 to-gray-300 rounded mr-2"></div>
                <span>Planned Budget</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gradient-to-r from-green-500 to-green-400 rounded mr-2"></div>
                <span>Actual Spend (Under)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-red-400 rounded mr-2"></div>
                <span>Actual Spend (Over)</span>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded mr-2"></div>
                <span>Sales Growth %</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Average: {(monthlyTrends.reduce((acc, m) => acc + m.salesGrowth, 0) / monthlyTrends.length).toFixed(1)}%
              </p>
            </div>
          )}
        </div>
        
        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700 font-medium">Retail Walk-ins:</span>
              <div className="text-blue-900 font-semibold">{salesMetrics.retailWalkIns.total.toLocaleString()}</div>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Growth Rate:</span>
              <div className="text-blue-900 font-semibold">{salesMetrics.retailWalkIns.growth}%</div>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Conversion:</span>
              <div className="text-blue-900 font-semibold">{salesMetrics.conversionRate}%</div>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Avg Order:</span>
              <div className="text-blue-900 font-semibold">${salesMetrics.averageOrderValue}</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendsChart;